import { Controller } from '@hotwired/stimulus';
import Rails from "@rails/ujs";
import debounce from 'lodash.debounce';

export default class extends Controller {
  static targets = [ 'checkbox', 'query', 'clearButton' ];

  connect(){
    this.boundUpdateURL = this.updateURL.bind(this);
    this.element.addEventListener('turbo:submit-end', this.boundUpdateURL);
    this.processFilterCountMutation(this.element);
    this.debouncedSubmit = debounce(this.submit.bind(this), 400);
  }

  submit() {
    // Auto-submits fire in the cold modal too, so its counts update live — the
    // server skips the (missing) #search_results render for those (embedded=1).
    Rails.fire(this.element, 'submit');
  }

  // The cold modal (off the results page) has no #search_results region.
  get isCold() {
    return !document.getElementById('search_results');
  }

  clearQuery() {
    this.queryTarget.value = '';
    this.toggleClearButton();
    this.resetOrderForQueryChange();
    this.submit();
    setTimeout(() => {
      this.queryTarget.focus();
    }, 50);
  }

  // Changing the search term should return to relevance ranking. An earlier
  // Most Recent / Liked / Viewed choice stays checked in the form radios and
  // would otherwise keep overriding the server's query-aware default.
  // Leave radios unchecked (so order isn't submitted) but mark the effective
  // default with .is-default — turbo streams don't re-render the sort row.
  resetOrderForQueryChange() {
    const queryPresent = this.hasQueryTarget && this.queryTarget.value.trim() !== '';
    const defaultOrder = queryPresent ? 'most_relevant' : 'most_recent';

    this.element.querySelectorAll('input[name="order"]').forEach((radio) => {
      radio.checked = false;
      radio.closest('label')?.classList.toggle('is-default', radio.value === defaultOrder);
    });
  }

  toggleClearButton() {
    if (this.hasClearButtonTarget) {
      if (this.queryTarget.value.trim() === '') {
        this.clearButtonTarget.classList.add('hidden');
      } else {
        this.clearButtonTarget.classList.remove('hidden');
      }
    }
  }

  updateURL(event) {
    // No live submits happen in the cold modal; let Turbo own history there.
    if (this.isCold) return;
    const url = event.detail.fetchResponse.response.url;
    // Inside an open mobile filter modal each submit is exploratory: replace the
    // history entry so cancel/back land on the committed URL, not a half-built
    // query (modal_filters_controller sets this flag while the modal is open).
    const method = this.element.dataset.modalSession ? 'replaceState' : 'pushState';
    window.history[method](window.history.state, null, url);
  }

  queryTargetConnected(target) {
    target.addEventListener('input', () => {
      this.toggleClearButton();
      this.resetOrderForQueryChange();
      this.debouncedSubmit();
    });

    // Enter/"Go" on the (mobile) keyboard signals the user is done typing: run
    // the search immediately and blur the input so the keyboard drops, revealing
    // the results and the "Show results" button. Without the blur the keyboard
    // stays up covering most of the modal, and nothing visibly happens on Enter.
    // preventDefault stops the native form submit that would double-fire here.
    target.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.debouncedSubmit.cancel();
        this.resetOrderForQueryChange();
        this.submit();
        target.blur();
      }
    });
  }

  checkboxTargetConnected(target) {
    target.addEventListener('change', () => {
      this.submit();
    });
  }

  processFilterCountMutation(element) {
    const config = { childList: true, subtree: true, characterData: true };

    // Callback function to execute when mutations are observed
    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if ( mutation.target.classList.contains('filter-count') ){
          const activeFilterCount = parseInt(mutation.target.innerHTML);
          if ( activeFilterCount > 0){
            mutation.target.classList.remove('hidden');
          } else {
            mutation.target.classList.add('hidden');
          }
        }
      }
    };

    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(callback);

    // Start observing the target node for configured mutations
    observer.observe(element, config);

    // Later, you can stop observing
    // observer.disconnect();
  }
}
